import { useState } from "react";
import { confirmSignIn } from "aws-amplify/auth";
import { PasswordInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { z } from "zod";
import { usePostHog } from "posthog-js/react";
import { AuthEvent } from "../../../data/auth-events";
import { passwordWithRequirementsSchema } from "./validation-schemas";
import PasswordRequirements from "./PasswordRequirements";
import styles from "./claimable-authenticator-v2.module.scss";

const forceNewPasswordSchema = z
  .object({
    password: passwordWithRequirementsSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ForceNewPasswordFormValues = z.infer<typeof forceNewPasswordSchema>;

type ForceNewPasswordFormProps = {
  email: string;
  onBackToSignIn: () => void;
};

const isSessionExpiredError = (message: string): boolean => {
  const lower = message.toLowerCase();
  return lower.includes("session") && lower.includes("expired");
};

const ForceNewPasswordForm = ({
  email,
  onBackToSignIn,
}: ForceNewPasswordFormProps) => {
  const posthog = usePostHog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<ForceNewPasswordFormValues>({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validate: zod4Resolver(forceNewPasswordSchema),
  });

  const handleSubmit = async (values: ForceNewPasswordFormValues) => {
    if (isSubmitting) return;

    posthog?.capture(AuthEvent.ForceNewPasswordSubmit);
    setIsSubmitting(true);
    setError(null);

    try {
      await confirmSignIn({ challengeResponse: values.password });
      posthog?.capture(AuthEvent.ForceNewPasswordSuccess);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An error occurred";
      posthog?.capture(AuthEvent.ForceNewPasswordError, {
        error: errorMessage,
      });

      if (isSessionExpiredError(errorMessage)) {
        setError("Your session has expired. Please sign in again.");
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToSignIn = () => {
    onBackToSignIn();
  };

  return (
    <div className={styles.forceNewPasswordForm}>
      <img
        src="/images/logo/logo_black_large.svg"
        alt="Claimable"
        className={styles.formLogo}
      />
      <h1 className={styles.formTitle}>{"Set New Password"}</h1>

      <p className={styles.formSubtitle}>
        {"Your account "}
        <strong>{email}</strong>
        {" requires a new password."}
      </p>

      <form onSubmit={form.onSubmit(handleSubmit)} className={styles.form}>
        <div className={styles.inputGroup}>
          <PasswordInput
            label="New Password"
            placeholder="Enter new password"
            size="lg"
            data-autofocus
            value={form.values.password}
            onChange={(e) =>
              form.setFieldValue("password", e.currentTarget.value)
            }
            error={
              form.isTouched("password") && form.errors.password ? true : false
            }
            classNames={{
              input: styles.mantineInput,
              label: styles.inputLabel,
            }}
          />
          <PasswordRequirements password={form.values.password} />
        </div>

        <PasswordInput
          label="Confirm Password"
          placeholder="Confirm new password"
          size="lg"
          value={form.values.confirmPassword}
          onChange={(e) =>
            form.setFieldValue("confirmPassword", e.currentTarget.value)
          }
          onBlur={() => form.validateField("confirmPassword")}
          error={form.errors.confirmPassword}
          classNames={{
            input: styles.mantineInput,
            label: styles.inputLabel,
          }}
        />

        {error && <p className={styles.formError}>{error}</p>}

        <button
          type="submit"
          className={styles.primaryButton}
          disabled={isSubmitting}
          data-testid="force-new-password-submit"
        >
          {isSubmitting ? "SETTING PASSWORD..." : "SET PASSWORD"}
        </button>
      </form>

      <div className={styles.switchAuthMode}>
        <button
          type="button"
          onClick={handleBackToSignIn}
          className={styles.switchLink}
        >
          {"Back to Sign In"}
        </button>
      </div>
    </div>
  );
};

export default ForceNewPasswordForm;
